import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { GridToolbarContainer } from '@mui/x-data-grid';
import { useMutation } from '@tanstack/react-query';
import { fetchBestMatchIds, MapResp } from 'api/cellRules';
import { DownloadCsvButton } from 'components/atoms/BaseDataGrid/DownloadCsvButton';
import AlertDialog from 'components/atoms/Dialog/AlertDialog';
import { CellRuleValue, MinimalCellRule } from 'interfaces/cellRule';
import { CellType } from 'interfaces/cellType';
import { filter, find, first, includes, isEmpty, map, size, slice, toLower } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import readXlsxFile from 'read-excel-file';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import CellSelectDropdown from '../CellRuleOptions/CellSelectDropdown';
import { useCellRulesExportActions } from './CellRulesExportOptions';
import NewIdsModal from './NewIdsModal';

export interface CellRulesEditableToolbarProps {
  editable: boolean;
  onImport?: (newStainTypeIds: string[], newCellTypeIds: string[], newRules: MinimalCellRule[]) => void;
  studyId?: string;
  panelId?: string;
  cellTypeIds?: string[];
  onAddCellTypes?: (cellTypeIds: string[]) => void;
  classAdditionDisabled?: boolean;
}

export const CustomToolbarWithExportAndImport = ({
  editable,
  onImport,
  studyId,
  panelId,
  cellTypeIds = [],
  onAddCellTypes,
  classAdditionDisabled = false,
}: CellRulesEditableToolbarProps) => {
  const { mifMarkerTypeOptionsWithoutDeprecated, isLoadingStainTypeOptions } = useStainTypeOptions();
  const { data: cellOptions, isLoading: isLoadingCellOptions } = useCellTypeOptions();
  const { onDownloadCsv, additionalActions } = useCellRulesExportActions();
  const classPickerTitleId = React.useId();
  const [classPickerAnchor, setClassPickerAnchor] = React.useState<HTMLElement | null>(null);
  const [selectedClasses, setSelectedClasses] = React.useState<CellType[]>([]);
  const classesToAdd = filter(
    selectedClasses,
    (cellType) =>
      !includes(cellTypeIds, cellType.id) &&
      Boolean(find(cellOptions, (option) => option.id === cellType.id && !option.isAbstract))
  );
  const closeClassPicker = () => {
    setClassPickerAnchor(null);
    setSelectedClasses([]);
  };

  const [alertDialogOpen, setAlertDialogOpen] = React.useState(false);
  const [alertMessages, setAlertMessages] = React.useState<string[]>([]);
  const [stainIdsFromFile, setStainIdsFromFile] = React.useState<string[]>([]);
  const [cellIdsFromFile, setCellIdsFromFile] = React.useState<string[]>([]);
  const [cellRulesFromFile, setCellRulesFromFile] = React.useState<MinimalCellRule[]>([]);
  const [isLoadingFileImport, setIsLoadingFileImport] = React.useState(false);
  const [isUnknownIdsModalOpen, setIsUnknownIdsModalOpen] = React.useState(false);
  const [isMatchingIdsModalOpen, setIsMatchingIdsModalOpen] = React.useState(false);
  const valuesForUnknownCellIdsStateHandlers = React.useState<{ [cellId: string]: string }>({});
  const valuesForUnknownStainIdsStateHandlers = React.useState<{ [stainId: string]: string }>({});
  const [valuesForUnknownCellIds, setValuesForUnknownCellIds] = valuesForUnknownCellIdsStateHandlers;
  const [valuesForUnknownStainIds, setValuesForUnknownStainIds] = valuesForUnknownStainIdsStateHandlers;

  const handleAlertDialogClose = () => {
    setAlertDialogOpen(false);
    setIsLoadingFileImport(false);
    setAlertMessages([]);
  };

  const matchIdsMutation = useMutation({
    mutationFn: fetchBestMatchIds,
    onSuccess: async (data: MapResp) => {
      setIsUnknownIdsModalOpen(false);
      setIsMatchingIdsModalOpen(true);
      setValuesForUnknownCellIds((prev) => {
        const updated = { ...prev };
        map(data.results, (result) => {
          if (result.match && result.type === 'cell' && result.match.id) {
            updated[result.input] = result.match.id;
          }
        });
        return updated;
      });
      setValuesForUnknownStainIds((prev) => {
        const updated = { ...prev };
        map(data.results, (result) => {
          if (result.match && result.type === 'stain' && result.match.id) {
            updated[result.input] = result.match.id;
          }
        });
        return updated;
      });
    },
    onError: (error) => {
      console.error('Error matching IDs:', error);
      setIsUnknownIdsModalOpen(false);
      setIsMatchingIdsModalOpen(true);
      enqueueSnackbar('Error occurred while matching unknown IDs. Please define them manually.', { variant: 'error' });
    },
  });

  // Access the last variables used in the mutation- this is not documented in react-query currently, but documented in version 5 of react-query
  const matchIdsMutationVariables = matchIdsMutation.variables;

  const onSetNewValuesForUnknownIds = (newValuesForUnknownIds: {
    valuesForUnknownCellIds: { [cellId: string]: string };
    valuesForUnknownStainIds: { [stainId: string]: string };
  }) => {
    const newStainIds = map(stainIdsFromFile, (stainId) => {
      const newStainId = newValuesForUnknownIds.valuesForUnknownStainIds[stainId];
      return newStainId ?? stainId;
    });
    const newCellIds = map(cellIdsFromFile, (cellId) => {
      const newCellId = newValuesForUnknownIds.valuesForUnknownCellIds[cellId];
      return newCellId ?? cellId;
    });
    const newRules = map(cellRulesFromFile, (rule) => {
      const stainId = find(newStainIds, (id) => id === rule.stainTypeId);
      const cellId = find(newCellIds, (id) => id === rule.cellTypeId);
      const newStainId = newValuesForUnknownIds.valuesForUnknownStainIds[rule.stainTypeId];
      const newCellId = newValuesForUnknownIds.valuesForUnknownCellIds[rule.cellTypeId];

      return {
        ...rule,
        stainTypeId: newStainId ?? stainId ?? rule.stainTypeId,
        cellTypeId: newCellId ?? cellId ?? rule.cellTypeId,
      };
    });

    onImport(newStainIds, newCellIds, newRules);
    setIsLoadingFileImport(false);
    setIsMatchingIdsModalOpen(false);
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoadingFileImport(true);
    const file = first(event.target.files);
    if (!file) {
      setIsLoadingFileImport(false);
      return;
    }
    // Reset the input value so the same file can be selected again
    event.target.value = '';

    try {
      // Parse Excel file using read-excel-file
      const rows = await readXlsxFile(file);

      if (size(rows) > 0) {
        const headerRow = first(rows) as string[];
        const tmpStainIFromFile = slice(headerRow, 1); // First column is for cell ids
        const tmpStainIdsFromFile = map(
          tmpStainIFromFile,
          (stainId) =>
            find(
              mifMarkerTypeOptionsWithoutDeprecated,
              (option) => option.id === stainId || option.displayName === stainId
            )?.id || stainId
        );

        const tmpCellIdsFromFile: string[] = [];
        const newRules: MinimalCellRule[] = [];

        const tempAlertMessages: string[] = [];
        const wrongCellIds: string[] = [];
        const wrongStainIds: string[] = filter(
          tmpStainIdsFromFile,
          (stainId) => !find(mifMarkerTypeOptionsWithoutDeprecated, (option) => option.id === stainId)
        );

        // Process each subsequent row
        for (let rowIndex = 1; rowIndex < size(rows); rowIndex++) {
          const row = rows[rowIndex] as (string | number)[];
          const cellTypeId = first(row) as string;
          if (cellTypeId) {
            const dbCell = find(
              cellOptions,
              (option) =>
                toLower(option.id) === toLower(cellTypeId) ||
                toLower(option.shortId) === toLower(cellTypeId) ||
                toLower(option.displayName) === toLower(cellTypeId)
            );
            if (!dbCell) {
              wrongCellIds.push(cellTypeId);
            }
            tmpCellIdsFromFile.push(dbCell?.id ?? cellTypeId);
            for (let columnIndex = 1; columnIndex < size(row); columnIndex++) {
              const stainTypeId =
                size(tmpStainIdsFromFile) >= columnIndex ? tmpStainIdsFromFile[columnIndex - 1] : undefined;
              if (!stainTypeId) {
                tempAlertMessages.push('One of the marker IDs is missing in the header row.');
              } else {
                const cellRuleValue = row[columnIndex];
                if (typeof cellRuleValue === 'number' && cellRuleValue in CellRuleValue) {
                  if (cellRuleValue !== CellRuleValue.Indifferent) {
                    newRules.push({
                      cellTypeId: dbCell?.id ?? cellTypeId,
                      stainTypeId,
                      ruleValue: cellRuleValue as CellRuleValue,
                    });
                  }
                } else {
                  tempAlertMessages.push(
                    `Invalid cell rule value for ${cellTypeId} and ${stainTypeId}. Please ensure all values are valid (options: -1, 0, 1, 2).`
                  );
                }
              }
            }
          }
        }

        if (isEmpty(tempAlertMessages) && isEmpty(wrongStainIds) && isEmpty(wrongCellIds)) {
          onImport(tmpStainIdsFromFile, tmpCellIdsFromFile, newRules);
          setIsLoadingFileImport(false);
        } else if (!isEmpty(tempAlertMessages)) {
          setAlertMessages(tempAlertMessages);
          setAlertDialogOpen(true);
        } else {
          setStainIdsFromFile(tmpStainIdsFromFile);
          setCellIdsFromFile(tmpCellIdsFromFile);
          setCellRulesFromFile(newRules);
          setIsUnknownIdsModalOpen(true);
          matchIdsMutation.mutate({
            studyId,
            cellIds: wrongCellIds,
            stainIds: wrongStainIds,
          });
        }
      }
    } catch (error) {
      console.error('Error reading Excel file:', error);
      setIsLoadingFileImport(false);
      enqueueSnackbar('Failed to read the Excel file. Please ensure it is a valid Excel format.', { variant: 'error' });
    }
  };

  const getUnknownIdsComponent = (title: string, ids: string[]) => {
    return !isEmpty(ids) ? (
      <Grid>
        <Grid item>
          <Typography variant="subtitle2">{title}</Typography>
        </Grid>
        {map(ids, (id) => (
          <Grid item key={id}>
            <Typography variant="body2" component="div" ml={2}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                {id}
              </Box>
            </Typography>
          </Grid>
        ))}
      </Grid>
    ) : null;
  };

  return (
    <GridToolbarContainer>
      <DownloadCsvButton onDownloadCsv={onDownloadCsv} additionalActions={additionalActions} />
      {editable && onAddCellTypes && (
        <>
          <Button
            size="small"
            color="secondary"
            variant="text"
            startIcon={<AddIcon />}
            disabled={classAdditionDisabled || isLoadingCellOptions}
            onClick={(event) => setClassPickerAnchor(event.currentTarget)}
          >
            Add cell classes
          </Button>
          <Popover
            open={Boolean(classPickerAnchor)}
            anchorEl={classPickerAnchor}
            onClose={closeClassPicker}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Stack
              role="dialog"
              aria-labelledby={classPickerTitleId}
              spacing={2}
              sx={{ p: 2, width: 360, maxWidth: '100%' }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography id={classPickerTitleId} variant="subtitle1">
                  Add cell classes
                </Typography>
                <IconButton size="small" aria-label="Close class selection" onClick={closeClassPicker}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              <CellSelectDropdown
                selectedCellTypeIds={map(selectedClasses, 'id')}
                onSelectCellTypes={setSelectedClasses}
                disabledCellTypeIds={cellTypeIds}
                excludeAbstract
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button color="inherit" onClick={closeClassPicker}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={classAdditionDisabled || isLoadingCellOptions || isEmpty(classesToAdd)}
                  onClick={() => {
                    if (classAdditionDisabled || isLoadingCellOptions || isEmpty(classesToAdd)) return;
                    onAddCellTypes(map(classesToAdd, 'id'));
                    closeClassPicker();
                  }}
                >
                  Add classes
                </Button>
              </Stack>
            </Stack>
          </Popover>
        </>
      )}
      {editable && onImport && (
        <>
          <input
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            id={`excel-file-input-${panelId}`}
            type="file"
            onChange={handleImportFile}
          />
          <label htmlFor={`excel-file-input-${panelId}`}>
            <Button
              variant="text"
              component="span"
              startIcon={<UploadIcon />}
              sx={{ ml: 1 }}
              disabled={isLoadingStainTypeOptions || isLoadingCellOptions || isLoadingFileImport}
            >
              Import Excel
            </Button>
          </label>
          <AlertDialog
            open={alertDialogOpen}
            onClose={handleAlertDialogClose}
            title="Invalid input"
            message={alertMessages}
            severity="warning"
          />
          {isUnknownIdsModalOpen && (
            <Dialog open={true}>
              <DialogTitle>Found Unknown IDs</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  We are comparing the markers and the cells you uploaded to our saved data to correct typos and
                  aliases:
                </DialogContentText>
                <Box
                  sx={{
                    backgroundColor: 'grey.100',
                    borderRadius: 1,
                    mt: 2,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <Grid container direction="column">
                    {getUnknownIdsComponent('Cell Type IDs:', matchIdsMutationVariables?.cellIds || [])}
                    {getUnknownIdsComponent('Markers IDs:', matchIdsMutationVariables?.stainIds || [])}
                  </Grid>
                </Box>
                <DialogContentText mt={2}>
                  Please wait...
                  <CircularProgress size={24} sx={{ ml: 1 }} />
                </DialogContentText>
              </DialogContent>
            </Dialog>
          )}
          {isMatchingIdsModalOpen && (
            <NewIdsModal
              unknownCellIds={matchIdsMutationVariables?.cellIds || []}
              valuesForUnknownCellIdsStateHandlers={valuesForUnknownCellIdsStateHandlers}
              unknownStainIds={matchIdsMutationVariables?.stainIds || []}
              valuesForUnknownStainIdsStateHandlers={valuesForUnknownStainIdsStateHandlers}
              onContinue={onSetNewValuesForUnknownIds}
              onCancel={() => {
                setIsLoadingFileImport(false);
                setIsMatchingIdsModalOpen(false);
              }}
            />
          )}
        </>
      )}
    </GridToolbarContainer>
  );
};

const ConfirmationModalBody: React.FC<{
  mapResponse: MapResp;
}> = ({ mapResponse }) => (
  <Grid container direction="row" columns={12} spacing={1}>
    {map(mapResponse?.results, (result) => (
      <Grid item xs={12} key={result.input}>
        <Typography variant="body2">
          {result.input} - {result?.match?.displayName || 'No Match'}
        </Typography>
      </Grid>
    ))}
  </Grid>
);
